/* Sales Ladder → Advanced: the sales strategy prompt (and, in Full automation,
   the account-level offer framing), then Negotiation and Alerts as titled
   sections. Everything here lives on the per-account sales policy. */

import { useTranslation } from 'react-i18next';
import type { OnMax, SNegotiation, SPolicy } from '../../Content/salesB/model';
import { Card, NumInput, Row, Section, Seg, TextArea, Toggle } from '../kit';
import { Framing } from './StagesSection';

const ON_MAX: OnMax[] = ['adjust_price_to_floor', 'continue', 'exit_to_communication', 'notify'];

export function PolicyAdvanced({ s, set, setNego, setNow, framing }: {
  s: SPolicy;
  set: (p: Partial<SPolicy>) => void;
  setNego: (p: Partial<SNegotiation>) => void;
  /** Save straight away (toggles). */
  setNow: (p: Partial<SPolicy>) => void;
  /** Full automation: the account-level offer framing, edited above the prompt. */
  framing?: { value: string; onChange: (v: string) => void };
}) {
  const { t } = useTranslation('assistantCompact');
  const n = s.negotiation;
  const alerts: { k: 'complete' | 'decline' | 'busy' | 'gone'; on: boolean; set: (v: boolean) => void }[] = [
    { k: 'complete', on: s.notifyLadderComplete, set: (v) => setNow({ notifyLadderComplete: v }) },
    { k: 'decline', on: n.notifyHardDecline, set: (v) => setNow({ negotiation: { ...n, notifyHardDecline: v } }) },
    { k: 'busy', on: n.notifyBusy, set: (v) => setNow({ negotiation: { ...n, notifyBusy: v } }) },
    { k: 'gone', on: n.notifyDisengage, set: (v) => setNow({ negotiation: { ...n, notifyDisengage: v } }) },
  ];
  return (
    <>
      <div data-tutorial="sl-prompts">
        <Card>
          {framing && (
            <div style={{ padding: '10px 14px' }}>
              <Framing title={t('sl.framing')} hint={t('sl.autoFramingHint')} value={framing.value} onChange={framing.onChange} height={88} />
            </div>
          )}
          <Row stack label={t('sl.adv.strategy')} help={t('sl.adv.strategyHint')}>
            <TextArea value={s.strategyPrompt} onChange={(v) => set({ strategyPrompt: v })} placeholder={t('sl.adv.strategyPh')} height={88} />
          </Row>
        </Card>
      </div>
      <div data-tutorial="sl-negotiation">
        <Section title={t('sl.neg.title')} hint={t('sl.neg.hint')}>
          <Card>
            <Row label={t('sl.neg.absMin')} help={t('sl.neg.absMinHelp')}>
              $<NumInput value={n.absMinPrice} onChange={(v) => setNego({ absMinPrice: v })} min={0} max={99999} allowEmpty placeholder={t('none')} width={64} />
            </Row>
            <Row label={t('sl.neg.tries')} help={t('sl.neg.triesHelp')}>
              <NumInput value={n.maxAttempts} onChange={(v) => v !== null && setNego({ maxAttempts: v })} min={0} max={5} int />
            </Row>
            <Row label={t('sl.neg.onMax')} help={t(`sl.neg.onMaxHelp.${n.onMax}`)}>
              <Seg value={n.onMax} onChange={(v) => setNego({ onMax: v })} options={ON_MAX.map((id) => ({ id, label: t(`sl.neg.onMaxOpt.${id}`) }))} />
            </Row>
            <Row label={t('sl.neg.grace')} help={t('sl.neg.graceHelp')}>
              <NumInput value={n.graceTurns} onChange={(v) => v !== null && setNego({ graceTurns: v })} min={0} max={20} int />
              <span>{t('turns')}</span>
            </Row>
            <Row label={t('sl.neg.turns')} help={t('sl.neg.turnsHelp')}>
              <NumInput value={n.maxTurns} onChange={(v) => v !== null && setNego({ maxTurns: v })} min={1} max={20} int />
              <span>{t('turns')}</span>
            </Row>
            <Row stack label={t('sl.neg.prompt')} help={t('sl.neg.promptHint')}>
              <TextArea value={n.prompt} onChange={(v) => setNego({ prompt: v })} placeholder={t('sl.neg.promptPh')} height={88} />
            </Row>
          </Card>
        </Section>
      </div>
      <div data-tutorial="sl-alerts">
        <Section title={t('sl.al.title')} hint={t('sl.al.hint')}>
          <Card>
            {alerts.map((a) => (
              <Row key={a.k} label={t(`sl.al.${a.k}`)} help={t(`sl.al.${a.k}Help`)}>
                <Toggle on={a.on} onChange={a.set} />
              </Row>
            ))}
          </Card>
        </Section>
      </div>
    </>
  );
}
